// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

/**
 * CreditsContext - Centralized Credits State Management
 * Replaces global CreditsManager singleton with React Context
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import GameConfig from '../config/GameConfig';
import achievementManager from '../utils/system/AchievementManager';
import { useDebouncedStorage } from '../hooks/useDebouncedStorage';
import STORAGE_KEYS from '../utils/system/STORAGE_KEYS';
import { getMasteryBonus } from '../utils/game/PermanentProgressManager';

const CreditsContext = createContext(null);

export function CreditsProvider({ children }) {
  // Initialize from localStorage or defaults
  const [credits, setCredits] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.CREDITS);
    return saved !== null ? parseInt(saved, 10) : Math.round(GameConfig.credits.startingCredits * (1 + getMasteryBonus('special')));
  });

  // Keep achievement tracking immediate; debounce the localStorage write
  useEffect(() => {
    achievementManager.updateCredits(credits);
  }, [credits]);
  useDebouncedStorage(STORAGE_KEYS.CREDITS, credits, 2000);

  // Add credits
  const addCredits = useCallback((amount, source = 'unknown') => {
    setCredits(prev => {
      const newCredits = prev + amount;
      return Math.max(0, newCredits);
    });
  }, []);

  // Remove credits
  const removeCredits = useCallback((amount, reason = 'unknown') => {
    setCredits(prev => {
      const newCredits = prev - amount;
      return Math.max(0, newCredits);
    });
  }, []);

  // Set credits directly
  const setCreditsDirect = useCallback((amount) => {
    setCredits(Math.max(0, amount));
  }, []);

  // Check if we can afford something
  const canAfford = useCallback((amount) => {
    return credits >= amount;
  }, [credits]);

  // Purchase something (returns true if successful)
  const purchase = useCallback((amount, item = 'item') => {
    if (credits >= amount) {
      setCredits(prev => prev - amount);
      return true;
    }
    return false;
  }, [credits]);

  // Reset to defaults (for game reset)
  const resetCredits = useCallback(() => {
    setCredits(GameConfig.credits.startingCredits);
    localStorage.removeItem(STORAGE_KEYS.CREDITS);
  }, []);

  const value = {
    credits,
    addCredits,
    removeCredits,
    setCredits: setCreditsDirect,
    canAfford,
    purchase,
    resetCredits,
  };

  return (
    <CreditsContext.Provider value={value}>
      {children}
    </CreditsContext.Provider>
  );
}

// Custom hook to use credits context
export function useCredits() {
  const context = useContext(CreditsContext);
  if (!context) {
    throw new Error('useCredits must be used within a CreditsProvider');
  }
  return context;
}

export default CreditsContext;
